/**
 * Service for submitting AUP (Acceptable Use Policy) feedback to JSM.
 * This routes harmful content reports to a dedicated AUP endpoint.
 */

import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { FetchError, performHttpFetch } from '@atlassian/conversation-assistant-utils/httpUtils';

import { type FeedbackTrigger } from './types';

const AUP_FEEDBACK_ENTRYPOINT_ID = '1a3369da-be17-4f4d-9c11-bb2895beebc2';

const base64Encode = (str: string): string => {
	try {
		return btoa(unescape(encodeURIComponent(str)));
	} catch {
		return str;
	}
};

const JSM_FIELD_IDS = {
	summary: 'summary',
	description: 'description',
	customerName: 'customfield_10045',
	canBeContacted: 'customfield_10043',
	enrollInResearch: 'customfield_10044',
	feedbackContext: 'customfield_10047',
} as const;

const JSM_FIELD_VALUES = {
	canBeContactedAgree: [{ id: '10109' }],
	canBeContactedDecline: [{ id: '10111' }],
	enrollInResearchAgree: [{ id: '10110' }],
	enrollInResearchDecline: [{ id: '10112' }],
} as const;

export interface AUPFeedbackMetadata {
	aaid: string;
	assistantResponseId?: string;
	messageRequestId?: string;
	origin: string;
	feedbackSource?: string;
	feedbackTrigger?: FeedbackTrigger;
	conversationId?: string;
	traceId?: string;
	history?: string | object;
	isAupFeedback: true;
	minionAlias?: string;
}

export interface AUPFeedbackParams {
	metadata: AUPFeedbackMetadata;
	customerName?: string;
	email?: string;
	feedbackText?: string;
	includeInput?: boolean;
	enrollInResearch?: boolean;
}

interface FeedbackField {
	id: string;
	value: string | object | object[];
}

interface FeedbackBody {
	feedback: {
		entrypointId: string;
		entrypointUiVersion: string;
		fields: FeedbackField[];
	};
}

const PACKAGE_VERSION = '1.0.0';

export const submitAUPFeedbackToJSM = async ({
	metadata,
	customerName = 'unknown',
	email,
	feedbackText = '',
	includeInput = false,
	enrollInResearch = false,
}: AUPFeedbackParams): Promise<{ success: boolean }> => {
	const summary = feedbackText
		? feedbackText.replace(/\n/g, ' ').slice(0, 80)
		: 'Harmful Content Report';

	const description = `${feedbackText}

\`\`\`
${JSON.stringify(metadata, null, 2)}
\`\`\`
`;

	const canBeContactedValue = includeInput
		? JSM_FIELD_VALUES.canBeContactedAgree
		: JSM_FIELD_VALUES.canBeContactedDecline;

	const enrollInResearchValue = enrollInResearch
		? JSM_FIELD_VALUES.enrollInResearchAgree
		: JSM_FIELD_VALUES.enrollInResearchDecline;

	const feedbackContextParts: string[] = [];
	if (email) {
		feedbackContextParts.push(`email: ${email}`);
	}
	feedbackContextParts.push(`atlassian-account-id: ${metadata.aaid}`);
	if (metadata.origin) {
		feedbackContextParts.push(`origin: ${metadata.origin}`);
	}
	if (metadata.feedbackSource) {
		feedbackContextParts.push(`feedbackSource: ${metadata.feedbackSource}`);
	}
	if (metadata.feedbackTrigger) {
		feedbackContextParts.push(`feedbackTrigger: ${metadata.feedbackTrigger}`);
	}
	const feedbackContextValue = feedbackContextParts.join('\n');

	const fields: FeedbackField[] = [
		{
			id: JSM_FIELD_IDS.summary,
			value: summary,
		},
		{
			id: JSM_FIELD_IDS.description,
			value: description,
		},
		{
			id: 'aaidOrHash',
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} as any,
		{
			id: JSM_FIELD_IDS.customerName,
			value: customerName,
		},
		{
			id: JSM_FIELD_IDS.canBeContacted,
			value: canBeContactedValue,
		},
		{
			id: JSM_FIELD_IDS.enrollInResearch,
			value: enrollInResearchValue,
		},
		{
			id: JSM_FIELD_IDS.feedbackContext,
			value: feedbackContextValue,
		},
	];

	const body: FeedbackBody = {
		feedback: {
			entrypointId: AUP_FEEDBACK_ENTRYPOINT_ID,
			entrypointUiVersion: PACKAGE_VERSION,
			fields,
		},
	};

	const postData = base64Encode(JSON.stringify(body));

	try {
		await performHttpFetch({
			path: '/gateway/api/feedback-collector-api/v2/feedback',
			options: {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ data: postData }),
			},
		});
	} catch (err) {
		const statusCode = err instanceof FetchError ? err.errorAttributes?.statusCode : undefined;
		logException({
			exception: err instanceof Error ? err : new Error(String(err)),
			name: 'feedbackSubmissionError',
			tags: {
				feedbackSubmissionError: 'aup-feedback',
				...(statusCode ? { httpStatusCode: String(statusCode) } : {}),
			},
		});
		throw err;
	}

	return { success: true };
};
